import { Link } from 'react-router-dom';
import type { ProfessionalOnlineStatus } from '@klaro7/shared-types';

import { professionalStatusDisplay } from '@/features/professionals/lib/statusDisplay';
import { useProfessionalOnlineEligibility } from '../hooks/useProfessionalOnlineEligibility';
import { useProfessionalPresence } from '../hooks/useProfessionalPresence';
import {
  type ServiceChannelKey,
  useProfessionalServiceChannels,
} from '../hooks/useProfessionalServiceChannels';

const PRESENCE_OPTIONS: Array<{
  value: Exclude<ProfessionalOnlineStatus, 'busy'>;
  label: string;
  activeClass: string;
  dotClass: string;
}> = [
  {
    value: 'online',
    label: 'Online',
    activeClass: 'border-emerald-500 bg-emerald-50 text-emerald-700',
    dotClass: 'bg-emerald-500',
  },
  {
    value: 'paused',
    label: 'Pausa',
    activeClass: 'border-brand-orange bg-brand-orange/10 text-brand-orange',
    dotClass: 'bg-brand-orange',
  },
  {
    value: 'offline',
    label: 'Offline',
    activeClass: 'border-neutral-500 bg-neutral-100 text-neutral-800',
    dotClass: 'bg-neutral-400',
  },
];

const CHANNELS: Array<{ key: ServiceChannelKey; label: string }> = [
  { key: 'chat', label: 'Chat' },
  { key: 'audioCall', label: 'Chiamate' },
  { key: 'videoCall', label: 'Videochiamate' },
];

export function ProfessionalQuickControls() {
  const presence = useProfessionalPresence();
  const eligibility = useProfessionalOnlineEligibility();
  const serviceChannels = useProfessionalServiceChannels();
  const display = professionalStatusDisplay(presence.status);
  const isBusy = presence.confirmedStatus === 'busy';
  const hasActiveChannel = Object.values(serviceChannels.channels).some(Boolean);
  const channelsDisabled =
    serviceChannels.isLoading || serviceChannels.isError || serviceChannels.isUpdating;
  const channelSaveLabel = serviceChannels.isUpdating
    ? 'Salvataggio...'
    : serviceChannels.isLoading
      ? 'Caricamento...'
      : serviceChannels.isError
      ? 'Non disponibili'
      : serviceChannels.saved
        ? 'Salvato'
        : '';

  return (
    <div className="border-b border-neutral-100 px-3 py-3">
      <div className="mb-2 flex items-center justify-between px-1">
        <p className="text-xs font-semibold uppercase text-neutral-500">Disponibilità</p>
        <p className="flex items-center gap-1.5 text-xs font-medium text-neutral-600" aria-live="polite">
          <span
            className={`h-2 w-2 rounded-full ${presence.isLoading || presence.isError ? 'bg-neutral-300' : display.dotClass}`}
            aria-hidden="true"
          />
          {presence.isLoading
            ? 'Caricamento...'
            : presence.isError
              ? 'Non disponibile'
              : display.label}
        </p>
      </div>

      <fieldset aria-label="Disponibilità professionista" className="grid grid-cols-3 gap-1.5">
        {PRESENCE_OPTIONS.map((option) => {
          const checked = !isBusy && presence.status === option.value;
          const requiresEligibility =
            option.value === 'online' ||
            (option.value === 'paused' && presence.confirmedStatus === 'offline');
          const disabled =
            presence.isUpdating ||
            isBusy ||
            presence.isLoading ||
            presence.isError ||
            !presence.isConnected ||
            (requiresEligibility &&
              (eligibility.isLoading || eligibility.isError || !eligibility.canGoOnline)) ||
            (requiresEligibility &&
              (serviceChannels.isLoading || serviceChannels.isError)) ||
            (option.value === 'online' && !hasActiveChannel);

          return (
            <label key={option.value} className="block min-w-0">
              <input
                type="radio"
                name="professional-presence"
                value={option.value}
                checked={checked}
                disabled={disabled}
                onChange={() => presence.setStatus(option.value)}
                className="peer sr-only"
              />
              <span
                className={`flex h-11 cursor-pointer items-center justify-center gap-1.5 rounded-md border px-2 text-xs font-semibold transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-brand-teal peer-focus-visible:ring-offset-1 peer-disabled:cursor-not-allowed peer-disabled:opacity-45 ${
                  checked
                    ? option.activeClass
                    : disabled
                      ? 'border-neutral-200 bg-white text-neutral-600'
                      : 'border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50'
                }`}
              >
                <span className={`h-2 w-2 shrink-0 rounded-full ${option.dotClass}`} aria-hidden="true" />
                <span className="truncate">{option.label}</span>
              </span>
            </label>
          );
        })}
      </fieldset>

      <div className="min-h-6 px-1 pt-1.5 text-xs text-neutral-500" aria-live="polite">
        {isBusy ? (
          <span>Potrai cambiare stato al termine della sessione.</span>
        ) : presence.isLoading ? (
          <span>Caricamento stato...</span>
        ) : presence.isError ? (
          <span>Stato non disponibile. Riprova tra poco.</span>
        ) : !presence.isConnected ? (
          <span>Connessione in corso...</span>
        ) : eligibility.isError ? (
          <span>Requisiti non disponibili. Riprova tra poco.</span>
        ) : !eligibility.isLoading && !eligibility.canGoOnline ? (
          <Link
            to="/dashboard/profilo-fiscale"
            className="font-medium text-brand-teal-700 hover:underline"
          >
            Completa i requisiti per andare online
          </Link>
        ) : serviceChannels.isLoading ? (
          <span>Caricamento canali...</span>
        ) : serviceChannels.isError ? (
          <span>Canali non disponibili. Riprova tra poco.</span>
        ) : !hasActiveChannel ? (
          <span>Attiva almeno un canale per andare online.</span>
        ) : presence.isUpdating ? (
          <span>Aggiornamento stato...</span>
        ) : null}
      </div>

      <div className="mt-2 flex items-center justify-between px-1">
        <p className="text-xs font-semibold uppercase text-neutral-500">Canali offerti</p>
        <span className="text-xs text-neutral-400" aria-live="polite">
          {channelSaveLabel}
        </span>
      </div>

      <div
        className="mt-1"
        aria-busy={serviceChannels.isLoading || serviceChannels.isUpdating}
      >
        {CHANNELS.map((channel) => (
          <label
            key={channel.key}
            className={`flex min-h-11 items-center justify-between rounded-md px-1 text-sm text-neutral-700 transition-colors ${
              channelsDisabled
                ? 'cursor-not-allowed opacity-60'
                : 'cursor-pointer hover:bg-neutral-50'
            }`}
          >
            <span>{channel.label}</span>
            <span className="relative inline-flex h-6 w-11 shrink-0">
              <input
                type="checkbox"
                role="switch"
                aria-label={`Abilita ${channel.label}`}
                checked={serviceChannels.channels[channel.key]}
                disabled={channelsDisabled}
                onChange={(event) => serviceChannels.setChannel(channel.key, event.target.checked)}
                className="peer sr-only"
              />
              <span className="absolute inset-0 rounded-full bg-neutral-200 transition-colors peer-checked:bg-brand-teal peer-focus-visible:ring-2 peer-focus-visible:ring-brand-teal peer-focus-visible:ring-offset-2 peer-disabled:opacity-50" />
              <span className="pointer-events-none absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5" />
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}
